// Pure helpers for the WhatsApp inbox, in a module with no imports so Node's
// test runner can check them (web/test/whatsapp.test.ts).
//
// Nothing here computes a business fact. Whether the 24 hour window is open,
// when it closes, how many messages are unread and who the person is all come
// from wa_conversations_v1. These only turn those facts into words and colours.

export type ContactKind = 'driver' | 'owner' | 'driver_applicant' | 'owner_applicant' | 'contact' | 'unknown'

export type Conversation = {
  phone: string
  person_id: string | null
  display_name: string
  profile_name: string | null
  contact_kind: ContactKind
  record_route: string | null
  last_message_preview: string | null
  last_message_at: string | null
  last_direction: 'in' | 'out'
  unread_count: number
  last_inbound_at: string | null
  window_open: boolean
  window_closes_at: string | null
}

export type ThreadMessage = {
  message_key: string
  direction: 'in' | 'out'
  body: string | null
  message_type: string
  status: string
  message_at: string
  sent_at: string | null
  delivered_at: string | null
  read_at: string | null
  failed_at: string | null
  failure_reason: string | null
  media_id: string | null
  mime_type: string | null
  filename: string | null
  sent_by_name: string | null
  template_key: string | null
  handled_at: string | null
  handled_by_name: string | null
}

export type WaTemplate = {
  name: string
  language: string
  category: string | null
  header_text: string | null
  body: string
  footer: string | null
  named: boolean
  variable_names: string[]
  variable_count: number
  usable: boolean
  unusable_reason: string | null
}

export type ChipToneName = 'green' | 'red' | 'amber' | 'neutral' | 'brand'

const KIND_LABEL: Record<ContactKind, string> = {
  driver: 'Driver',
  owner: 'Owner',
  driver_applicant: 'Driver applicant',
  owner_applicant: 'Owner applicant',
  contact: 'On record',
  unknown: 'Unknown',
}

export function kindLabel(kind: string): string {
  return KIND_LABEL[kind as ContactKind] ?? 'Unknown'
}

export function kindTone(kind: string): ChipToneName {
  if (kind === 'driver') return 'green'
  if (kind === 'owner') return 'brand'
  if (kind === 'driver_applicant' || kind === 'owner_applicant') return 'amber'
  return 'neutral'
}

/**
 * Hours left in the 24 hour window, rounded down, or null when it is closed.
 * Under an hour reads as minutes, because "0h" next to a reply box invites a
 * reply that will be refused.
 */
export function windowLeft(
  c: Pick<Conversation, 'window_open' | 'window_closes_at'>,
  now: Date = new Date(),
): { open: boolean; label: string } {
  if (!c.window_open || !c.window_closes_at) return { open: false, label: 'Closed' }
  const ms = new Date(c.window_closes_at).getTime() - now.getTime()
  if (Number.isNaN(ms) || ms <= 0) return { open: false, label: 'Closed' }
  const minutes = Math.floor(ms / 60000)
  if (minutes < 60) return { open: true, label: `${Math.max(minutes, 1)}m left` }
  return { open: true, label: `${Math.floor(minutes / 60)}h left` }
}

export type Tick = {
  glyph: 'clock' | 'one' | 'two' | 'read' | 'failed'
  label: string
}

/** The delivery mark under an outbound message, and what it means in words. */
export function tickFor(m: Pick<ThreadMessage, 'status' | 'read_at' | 'failure_reason'>): Tick {
  if (m.status === 'failed' || m.status === 'bounced') {
    return {
      glyph: 'failed',
      label: m.failure_reason ? `Not delivered: ${m.failure_reason}` : 'Not delivered',
    }
  }
  if (m.read_at) return { glyph: 'read', label: 'Read' }
  if (m.status === 'delivered') return { glyph: 'two', label: 'Delivered' }
  if (m.status === 'sent') return { glyph: 'one', label: 'Sent' }
  return { glyph: 'clock', label: 'Sending' }
}

export type MediaKind = 'audio' | 'image' | 'video' | 'document' | null

export function mediaKind(m: Pick<ThreadMessage, 'message_type' | 'media_id'>): MediaKind {
  if (!m.media_id) return null
  if (m.message_type === 'audio') return 'audio'
  if (m.message_type === 'image' || m.message_type === 'sticker') return 'image'
  if (m.message_type === 'video') return 'video'
  return 'document'
}

// Meta withholds some message types from business accounts and sends a
// placeholder instead (error 131051). The two it has named so far, Oct 2026.
// wa_thread_v1 does not return the name yet, so until it does these read as
// the general note.
const WITHHELD_NAMED: Record<string, string> = {
  album: 'an album of photos',
  list: 'a list',
}

const CHECK_PHONE = 'Check the WhatsApp Business app on the phone.'

/**
 * What to show for an inbound message with no text and no attachment, and
 * whether it is a quiet line rather than something to act on. Written for the
 * person replying, so it says what to do next whether or not the phone app has
 * the message.
 */
export function unshownNote(
  m: Pick<ThreadMessage, 'message_type'> & { unsupported_type?: string | null },
): { text: string; quiet: boolean } {
  // Every `errors` row so far came from the history import (3,190 of them, not
  // one from the live webhook), and they are months old, so asking anyone to
  // resend would be wrong.
  if (m.message_type === 'errors') {
    return { text: "From imported history: this message's content wasn't available.", quiet: true }
  }
  if (m.message_type === 'revoke') return { text: 'They deleted this message.', quiet: true }
  if (m.message_type === 'edit') {
    return { text: "They edited a message. WhatsApp didn't pass on the new text.", quiet: true }
  }
  const named = m.unsupported_type ? WITHHELD_NAMED[m.unsupported_type] : undefined
  if (named) {
    return { text: `They sent ${named}, which WhatsApp doesn't pass on here. ${CHECK_PHONE}`, quiet: false }
  }
  return {
    text:
      "WhatsApp didn't pass this message on, so it can't be shown here. It may be a voice note, poll or " +
      `another type WhatsApp doesn't support for business accounts. ${CHECK_PHONE} ` +
      "If it isn't there either, ask them to resend it as text.",
    quiet: false,
  }
}

const TZ = 'Africa/Johannesburg'

/** Johannesburg time. "14:05" today, "Yesterday", "Mon", then "12 Aug". */
export function listTime(iso: string | null, now: Date = new Date()): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const day = (x: Date) => x.toLocaleDateString('en-CA', { timeZone: TZ })
  if (day(d) === day(now)) {
    return d.toLocaleTimeString('en-ZA', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false })
  }
  const y = new Date(now.getTime() - 86400000)
  if (day(d) === day(y)) return 'Yesterday'
  if (now.getTime() - d.getTime() < 6 * 86400000) {
    return d.toLocaleDateString('en-ZA', { timeZone: TZ, weekday: 'short' })
  }
  return d.toLocaleDateString('en-ZA', { timeZone: TZ, day: 'numeric', month: 'short' })
}

/** Full Johannesburg time for a message, "28 Sep 14:05". */
export function messageTime(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const date = d.toLocaleDateString('en-ZA', { timeZone: TZ, day: 'numeric', month: 'short' })
  const time = d.toLocaleTimeString('en-ZA', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return `${date} ${time}`
}

/** "+27 71 190 1876" for a South African number, "+<digits>" otherwise. */
export function formatPhone(digits: string): string {
  if (/^27\d{9}$/.test(digits)) {
    return `+27 ${digits.slice(2, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`
  }
  return `+${digits}`
}

/** A template body with its fields filled, or left as the field name when blank. */
export function previewTemplate(
  t: Pick<WaTemplate, 'header_text' | 'body' | 'footer' | 'variable_names'>,
  values: string[],
): string {
  const byName = new Map(t.variable_names.map((n, i) => [n, values[i] ?? '']))
  const body = t.body.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (_m, n: string) => {
    const v = (byName.get(n) ?? '').trim()
    return v || `[${/^\d+$/.test(n) ? `field ${n}` : n}]`
  })
  return [t.header_text, body, t.footer].filter((s) => s && s.trim() !== '').join('\n\n')
}
